import { BarChartOutlined } from '@ant-design/icons'
import { Avatar, Button, Col, Divider, message, Row, Tag } from 'antd'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'
import { evoClient } from '$api/client'
import { fetchFileAsServiceUser, fetchServiceConfigByName } from '$api/evoAPIs'
import { parts } from '$api/client'
import DownloadImage from './DownloadImage'
import Partimage from '$components/Services/PartImage/Partimage'
import Progresssteps from './Progresssteps'
import { ExpandableTableCard, LabelText } from './styledComponents'
import { StyledGlobal } from '$globalStyledComponents/Global'
import { convertStatusInfoToStep } from '$utils'
import { useTranslation } from 'react-i18next'
import useFetchDownloadQuery from '$components/FileDownload/useFetchDownloadQuery'
import { useTheme } from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'

const Partdetails = ({ record }) => {
  const [current, setCurrent] = useState(0)
  const [partPng, setPartPng] = useState('')
  const { t } = useTranslation()
  const theme = useTheme()
  const [messageApi, contextHolder] = message.useMessage()

  const downloadFile = useFetchDownloadQuery()

  const { data: serviceConfig, isFetching: isFetchingServiceConfig } = useQuery(
    {
      queryKey: ['serviceConfig', record.id],
      queryFn: () => fetchServiceConfigByName(record.service_name),
      enabled: [
        'Apply Branding',
        'Apply Texturing',
        'Generate 3D Model',
      ].includes(record.service_name),
    },
  )

  const showPartImagePreview =
    !isFetchingServiceConfig &&
    (!serviceConfig || serviceConfig.show_part_preview)

  const getSteps = () => {
    if (record.status_info === 'rejected') {
      return [
        {
          title: t('bookedservices.status.placed'),
          description: t('bookedservices.order_placed_by_customer'),
        },
        {
          title: 'Rejected',
          description: 'Order rejected by provider',
        },
      ]
    }

    return [
      {
        title: t('bookedservices.status.placed'),
        description: t('bookedservices.order_placed_by_customer'),
      },
      {
        title: t('bookedservices.status.in_progress'),
        description: t('bookedservices.order_is_being_processed'),
      },
      {
        title: t('bookedservices.status.delivery'),
        description: t('bookedservices.order_in_delivery'),
      },
    ]
  }

  const steps = getSteps()

  useEffect(() => {
    dayjs.extend(advancedFormat)
    dayjs.extend(utc)
    dayjs.extend(timezone)
    dayjs.tz.guess()

    // set current state: in orderedservice view state 2 is not finished, state 3 is.
    // if (!record?.part_status === false) {
    if (convertStatusInfoToStep(record?.status_info) === 2) {
      setCurrent(convertStatusInfoToStep(record.status_info) - 1)
    } else if (convertStatusInfoToStep(record?.status_info) === 3) {
      setCurrent(2)
    } else {
      setCurrent(convertStatusInfoToStep(record?.status_info))
    }
    // }
    // this is only when its a feasibility check
    if (record?.status && record?.status === 100) {
      setCurrent(2)
    }

    // get the part image from client
    evoClient
      .get(`${parts}${record.part.db_id_client}/png/`, {
        responseType: 'blob',
      })
      .then(async (resp) => {
        const asyncBlob = await resp.data.arrayBuffer()
        const blob = new Blob([asyncBlob], { type: 'image/png' })
        setPartPng(blob)
      })
      .catch((error) => {
        // handle error
        console.error(error)
      })
  }, [record.part, record?.status, record?.status_info])

  const downloadFinalFile = () => {
    const { part, provider_file_location: fileName } = record
    if (!part.db_id_client || !fileName) {
      messageApi.error(t('download_error'))
      return
    }

    downloadFile({
      queryKey: ['parts', part.db_id_client, 'file', fileName],
      queryFn: fetchFileAsServiceUser,
      downloadFileName: fileName,
      onError: () => messageApi.error(t('download_error')),
    })
  }

  const renderFCResult = () => {
    const fCValue = record.feasible_printers.length > 0
    const fill = fCValue
      ? (theme.colorBrightGreen500 ?? '#34C38F')
      : (theme.colorBrightRed500 ?? '#CD0204')
    return (
      <div style={{ display: 'flex' }}>
        <StyledGlobal className="traffic-light">
          <svg viewBox="0 0 12 12">
            <circle cx="60%" cy="60%" r="4" fill={fill} />
          </svg>
        </StyledGlobal>
        <span style={{ marginTop: '1px' }}>
          {fCValue ? 'feasible' : 'not feasible'}
        </span>
      </div>
    )
  }

  const isBrandedOrTextured =
    record.service_name === 'Apply Branding'
      ? { branded: true }
      : record.service_name === 'Apply Texturing'
        ? { textured: true }
        : null

  const getStepContent = () => {
    switch (current) {
      case 0:
        return (
          <Partimage
            partPng={partPng}
            isBrandedOrTextured={isBrandedOrTextured}
          />
        )
      case 1:
        return (
          <Partimage
            partPng={partPng}
            isBrandedOrTextured={isBrandedOrTextured}
          />
        )
      case 2:
        return <DownloadImage />

      default:
        return (
          <Partimage
            partPng={partPng}
            isBrandedOrTextured={isBrandedOrTextured}
          />
        )
    }
  }
  const getStepContent2 = () => {
    switch (current) {
      case 0:
        return (
          <p style={{ width: 187 }}>
            {t('bookedservices.service_ordered_waiting')}
          </p>
        )
      case 1:
        return (
          <>
            <Row>
              <Col span={10}>
                <LabelText style={{ marginBottom: '2px' }}>
                  {t('bookedservices.date_of_request')}:
                </LabelText>
              </Col>
              <Col>
                <p>
                  {dayjs(record.time_stamp).format('DD.MM.YYYY, k:mm:ss, z')}
                </p>
              </Col>
            </Row>
            <Row>
              <Col span={10}>
                <LabelText style={{ marginBottom: '2px' }}>
                  {t('bookedservices.time_passed')}:
                </LabelText>
              </Col>
              <Col>
                <p>{dayjs(record.time_stamp).fromNow()}</p>
              </Col>
            </Row>
            {record?.status_info === 'in_delivery' && (
              <Row>
                <Col span={10}>
                  <LabelText style={{ marginBottom: '2px' }}>
                    {t('received_requests.status')}:
                  </LabelText>
                </Col>
                <Col>
                  <Tag>{t('bookedservices.delivering')}</Tag>
                </Col>
              </Row>
            )}
            {record?.status_info === 'rejected' && (
              <>
                <Divider />
                <LabelText>{t('info')}</LabelText>
                <p
                  style={{
                    color: theme.colorBrightRed500 ?? 'red',
                    fontSize: '15px',
                  }}
                >
                  Order Rejected
                </p>
                <p
                  style={{
                    color: theme.colorBrightRed500 ?? 'red',
                    fontSize: '12px',
                  }}
                >
                  {record.provider_rejection_reason}
                </p>
              </>
            )}
          </>
        )
      case 2:
        return (
          <>
            <Row>
              <Col span={10}>
                <LabelText style={{ marginBottom: '2px' }}>
                  {t('bookedservices.date_of_request')}:
                </LabelText>
              </Col>
              <Col>
                <p>
                  {dayjs(record.time_stamp).format('DD.MM.YYYY, k:mm:ss, z')}
                </p>
              </Col>
            </Row>
            {record.provider_file_location ? (
              <>
                <Row>
                  <Col span={10}>
                    <LabelText style={{ marginBottom: '2px' }}>
                      {t('marketplace.result')}:
                    </LabelText>
                  </Col>
                  <Col>
                    {record.feasible_printers ? (
                      renderFCResult()
                    ) : (
                      <Button
                        disabled={
                          record?.status_info === 'finished' ? false : true
                        }
                        type="primary"
                        size="small"
                        onClick={downloadFinalFile}
                      >
                        {t('Download')}
                      </Button>
                    )}
                  </Col>
                </Row>
              </>
            ) : null}
          </>
        )

      default:
        return (
          <p>
            {t('bookedservices.status.unknown')} {current}.
          </p>
        )
    }
  }

  return (
    <>
      {contextHolder}
      <ExpandableTableCard>
        <Row className="row">
          {showPartImagePreview && (
            <Col span={9} style={{ padding: '0 30px 0 0' }}>
              {getStepContent()}
            </Col>
          )}
          <Col
            span={showPartImagePreview ? 10 : 19}
            style={{ padding: '0 30px 0 0' }}
          >
            <div style={{ display: 'flex' }}>
              <div style={{ margin: '-10px 10px 0 -8px' }}>
                <Avatar
                  icon={<BarChartOutlined />}
                  style={{
                    margin: '3px 3px 5px',
                    border: '5px solid white',
                    width: '52px',
                    height: '52px',
                    fontSize: '22px',
                    lineHeight: '43px',
                  }}
                />
              </div>
              <div>
                <LabelText style={{ marginBottom: '2px' }}>
                  {t('service.processed_by')}:
                </LabelText>
                <p>Daimler Buses GmbH</p>
                {/* {record.company.substring(1)} */}
              </div>
            </div>
            <Divider />
            {getStepContent2()}
            <Divider />
          </Col>
          <Col span={5}>
            <Progresssteps current={current} steps={steps} />
          </Col>
        </Row>
      </ExpandableTableCard>
    </>
  )
}

Partdetails.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    service_name: PropTypes.string,
    status_info: PropTypes.string,
    status: PropTypes.number,
    feasible_printers: PropTypes.array,
    time_stamp: PropTypes.string,
    provider_file_location: PropTypes.string,
    part: PropTypes.shape({
      db_id_client: PropTypes.string,
    }),
  }).isRequired,
}

export default Partdetails
